import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import DateTimePickerInput from '../DateTimePickerInput';

type Icon = React.ComponentProps<typeof Ionicons>['name'];
export function ActionButton({
  title,
  icon,
  onPress,
  disabled,
  tone = 'secondary',
  loading = false,
  accessibilityLabel,
}: {
  title: string;
  icon?: Icon;
  onPress: () => void;
  disabled?: boolean;
  tone?: 'primary' | 'secondary' | 'danger' | 'quiet';
  loading?: boolean;
  accessibilityLabel?: string;
}) {
  const color =
    tone === 'primary' ? '#FFFFFF' : tone === 'danger' ? '#B91C1C' : '#334155';
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? title}
      accessibilityState={{ disabled: !!disabled, busy: loading }}
      aria-disabled={!!disabled || loading}
      aria-busy={loading}
      disabled={disabled || loading}
      onPress={onPress}
      style={[
        s.button,
        tone === 'primary' && s.primary,
        tone === 'quiet' && s.quiet,
        disabled && s.disabled,
      ]}
    >
      {loading ? (
        <ActivityIndicator size="small" color={color} />
      ) : (
        icon && <Ionicons name={icon} size={18} color={color} />
      )}
      <Text style={[s.buttonText, { color }]}>{title}</Text>
    </Pressable>
  );
}

export function IconButton({
  label,
  icon,
  onPress,
  disabled,
}: {
  label: string;
  icon: Icon;
  onPress: () => void;
  disabled?: boolean;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ disabled: !!disabled }}
      aria-disabled={!!disabled}
      disabled={disabled}
      onPress={onPress}
      style={[s.iconButton, disabled && s.disabled]}
    >
      <Ionicons name={icon} size={20} color="#475569" />
    </Pressable>
  );
}

export function TaskField({
  label,
  value,
  onChange,
  numeric,
  multiline,
  date,
  error,
  hint,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  numeric?: boolean;
  multiline?: boolean;
  date?: boolean;
  error?: string;
  hint?: string;
}) {
  return (
    <View style={s.field}>
      {date ? (
        <DateTimePickerInput
          mode="date"
          label={label}
          value={value ? value.split('-').reverse().join('.') : ''}
          onChange={(value) => onChange(value.split('.').reverse().join('-'))}
        />
      ) : (
        <>
          <Text style={s.label}>{label}</Text>
          <TextInput
            accessibilityLabel={label}
            value={value}
            onChangeText={onChange}
            keyboardType={numeric ? 'number-pad' : 'default'}
            multiline={multiline}
            style={[s.input, multiline && s.multiline, !!error && s.invalid]}
          />
        </>
      )}
      {!!hint && <Text style={s.hint}>{hint}</Text>}
      {!!error && (
        <Text accessibilityRole="alert" style={s.error}>
          {error}
        </Text>
      )}
    </View>
  );
}

export function TaskChoice({
  label,
  value,
  options,
  onChange,
  placeholder,
  disabled,
  error,
}: {
  label: string;
  value: string;
  options: { id: string; name: string }[];
  onChange: (id: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const trigger = useRef<View>(null);
  const close = useCallback(() => {
    setOpen(false);
    trigger.current?.focus();
  }, []);
  useEffect(() => {
    if (!open || Platform.OS !== 'web' || typeof document === 'undefined')
      return;
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      close();
    };
    document.addEventListener('keyup', escape, true);
    return () => document.removeEventListener('keyup', escape, true);
  }, [open, close]);
  const filtered = options.filter((option) =>
    option.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
  );
  return (
    <View style={s.field}>
      <Text style={s.label}>{label}</Text>
      <Pressable
        ref={trigger}
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityState={{ expanded: open, disabled: !!disabled }}
        aria-expanded={open}
        aria-disabled={!!disabled}
        disabled={disabled}
        onPress={() => {
          setSearch('');
          setOpen(!open);
        }}
        style={[s.select, !!error && s.invalid, disabled && s.disabled]}
      >
        <Text style={[s.flex, !value && s.hint]}>
          {options.find((option) => option.id === value)?.name ??
            placeholder ??
            '—'}
        </Text>
        <Ionicons
          name={open ? 'chevron-up' : 'chevron-down'}
          size={18}
          color="#64748B"
        />
      </Pressable>
      {open && (
        <View style={s.options}>
          <View style={s.searchRow}>
            <Ionicons name="search" size={18} color="#64748B" />
            <TextInput
              accessibilityLabel={t('operations.taskSearchChoice', label)}
              placeholder={t('operations.taskSearchPlaceholder')}
              value={search}
              onChangeText={setSearch}
              style={s.search}
            />
          </View>
          <ScrollView
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
            style={s.optionList}
          >
            {filtered.map((option) => (
              <Pressable
                key={option.id}
                accessibilityRole="radio"
                accessibilityLabel={option.name}
                accessibilityState={{ checked: value === option.id }}
                aria-checked={value === option.id}
                style={s.option}
                onPress={() => {
                  onChange(option.id);
                  close();
                }}
              >
                <Text style={s.flex}>{option.name}</Text>
                {value === option.id && (
                  <Ionicons name="checkmark" size={18} color="#2563EB" />
                )}
              </Pressable>
            ))}
            {!filtered.length && (
              <Text style={s.empty}>{t('operations.taskNoResults')}</Text>
            )}
          </ScrollView>
        </View>
      )}
      {!!error && (
        <Text accessibilityRole="alert" style={s.error}>
          {error}
        </Text>
      )}
    </View>
  );
}

export function Segments({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { id: string; name: string }[];
  onChange: (id: string) => void;
}) {
  return (
    <View style={s.field}>
      <Text style={s.label}>{label}</Text>
      <View
        accessibilityRole="radiogroup"
        accessibilityLabel={label}
        style={s.segments}
      >
        {options.map((option) => (
          <Pressable
            key={option.id}
            accessibilityRole="radio"
            accessibilityLabel={option.name}
            accessibilityState={{ checked: value === option.id }}
            aria-checked={value === option.id}
            onPress={() => onChange(option.id)}
            style={[s.segment, value === option.id && s.segmentActive]}
          >
            <Text
              style={[
                s.segmentText,
                value === option.id && s.segmentTextActive,
              ]}
            >
              {option.name}
            </Text>
          </Pressable>
        ))}
      </View>
    </View>
  );
}

const s = StyleSheet.create({
  field: { gap: 7 },
  label: { fontSize: 13, fontWeight: '600', color: '#334155' },
  hint: { fontSize: 12, color: '#64748B', lineHeight: 18 },
  error: { color: '#B91C1C', fontSize: 12, lineHeight: 18 },
  flex: { flex: 1, flexShrink: 1, color: '#334155' },
  input: {
    minHeight: 44,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    padding: 11,
    backgroundColor: '#FFFFFF',
    fontSize: 15,
    color: '#0F172A',
  },
  multiline: { minHeight: 90, textAlignVertical: 'top' },
  invalid: { borderColor: '#DC2626' },
  button: {
    minHeight: 44,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    backgroundColor: '#FFFFFF',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    alignSelf: 'flex-start',
    maxWidth: '100%',
  },
  primary: { backgroundColor: '#2563EB', borderColor: '#2563EB' },
  quiet: { borderColor: 'transparent', backgroundColor: 'transparent' },
  buttonText: { fontSize: 13, fontWeight: '600', flexShrink: 1 },
  disabled: { opacity: 0.45 },
  iconButton: {
    width: 44,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
  },
  select: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 44,
    gap: 8,
    padding: 11,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
  },
  options: {
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderColor: '#E2E8F0',
  },
  search: {
    flex: 1,
    minHeight: 44,
    paddingVertical: 10,
    color: '#0F172A',
    fontSize: 14,
  },
  optionList: { maxHeight: 220 },
  option: {
    minHeight: 44,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderBottomWidth: 1,
    borderColor: '#F1F5F9',
  },
  empty: { padding: 16, color: '#64748B' },
  segments: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  segment: {
    minHeight: 40,
    paddingHorizontal: 10,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: '#F1F5F9',
    justifyContent: 'center',
  },
  segmentActive: { backgroundColor: '#DBEAFE' },
  segmentText: { fontSize: 13, color: '#475569' },
  segmentTextActive: { color: '#1D4ED8', fontWeight: '600' },
});
